import React from 'react';
import { NavLink } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import TrackingHeader from 'components/ui/tracking-header';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import classNames from 'classnames';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { isApacProd, isProd } from 'helpers/helper_functions';
import { getCustomerReports } from 'utils/UnifiedViewWebAPIUtils';
import Navbar from '../v2/navs/Navbar';
import DropdownMenu from '../v2/dropdowns/DropdownMenu';
import UserActionCreators from '../../actions/UserActionCreators';
import WhiteLabel from '../../utils/whitelabel.js';
import NavbarOverlayBanner from '../v2/navbar-overlay-banner';
import ShipmentSearch from './shipment-search';
import { Avatar } from '../modules/user/Avatar';
import LanguageSelector from './language-selector';

function logout() {
  UserActionCreators.logout();
}

const isShipper = () => {
  return SessionStore.isShipper();
};

const isMasterAccountUser = () => {
  return SessionStore.isMasterAccountUser();
};

class ShipperNavigation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      logo: CargomaticLogo,
      reports: [],
    };
  }

  componentDidMount() {
    const logoFunction = async () => {
      const logo = await WhiteLabel.getClientLogo(false);
      if (logo) {
        this.setState({ logo });
      }
    };
    getCustomerReports()
      .then((reports) => {
        this.setState({ reports });
      })
      .catch((e) => console.error(e));
    logoFunction();
  }

  renderMobileNav = (onClose) => {
    const items = [];
    const { t } = this.props;

    if (!isMasterAccountUser()) {
      items.push(
        <NavLink
          className="nav-item"
          to="/quotes"
          onClick={onClose}
          key="mobile-quotes"
        >
          <span>{t('GET_QUOTE_SHIPMENT')}</span>
        </NavLink>,
        <NavLink
          className="nav-item"
          to="/booking"
          onClick={onClose}
          key="mobile-booking"
        >
          <span>{t('BOOK_SHIPMENT')}</span>
        </NavLink>,
      );
    }

    items.push(
      <NavLink
        className="nav-item"
        to="/shipments"
        onClick={onClose}
        key="mobile-shipments"
      >
        <span>{t('SHIPMENT_HISTORY')}</span>
      </NavLink>,
    );

    if (!isMasterAccountUser()) {
      items.push(
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <NavLink
            className="nav-item"
            to="/invoices"
            onClick={onClose}
            key="mobile-invoices"
          >
            <span>{t('MY_INVOICES')}</span>
          </NavLink>
        </AuthorizationWrapper>,
      );
    }

    if (isShipper()) {
      items.push(
        <NavLink
          className="nav-item"
          to="/accessorials"
          onClick={onClose}
          key="mobile-accessorials"
        >
          <span>{t('ACCESSORIAL', { count: 2 })}</span>
        </NavLink>,
      );
    }

    items.push(
      <NavLink
        className="nav-item"
        to="/profile"
        onClick={onClose}
        key="mobile-profile"
      >
        <span>{titleize(t('PROFILE', { count: 1 }))}</span>
      </NavLink>,
    );

    items.push(
      <div
        className={classNames('flex items-center justify-center nav-item', {
          hidden: LANGUAGE_SELECTION_DISABLED,
        })}
      >
        <LanguageSelector mobile />
      </div>,
    );

    items.push(
      <a href="#" className="nav-item" onClick={logout} key="mobile-sign-out">
        {t('SIGN_OUT')}
      </a>,
    );

    return items;
  };

  renderRightNav = () => {
    const { t } = this.props;
    const shipperName = SessionStore.getUser().first_name;
    const userId = SessionStore.getUser()._id;

    const profileButton = (
      <div
        title={shipperName}
        className="flex text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
      >
        <Avatar
          key={userId}
          userId={userId}
          altName="Logo"
          className="w-8 h-8 rounded-full"
        />
      </div>
    );

    return [
      <div
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu button={profileButton} key="right-nav">
        <Menu.Item key="profile">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/profile"
          >
            <span>{titleize(t('MANAGE'))}</span>
          </NavLink>
        </Menu.Item>
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <Menu.Item key="billing">
            <NavLink
              activeClassName="active-light"
              className="nav-item"
              to="/billing"
            >
              <span>{t('BILLING')}</span>
            </NavLink>
          </Menu.Item>
        </AuthorizationWrapper>
        <Menu.Item key="notifications">
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/notifications"
          >
            <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
          </NavLink>
        </Menu.Item>
        {!isMasterAccountUser() && (
          <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
            <Menu.Item key="invoices">
              <NavLink
                activeClassName="active-light"
                className="nav-item"
                to="/invoices"
              >
                <span>{t('MY_INVOICES')}</span>
              </NavLink>
            </Menu.Item>
          </AuthorizationWrapper>
        )}
        <Menu.Item key="sign-out">
          <a href="#" onClick={logout}>
            {t('SIGN_OUT')}
          </a>
        </Menu.Item>
      </DropdownMenu>,
    ];
  };

  renderMenuItemDashboard() {
    const { t } = this.props;
    const { reports } = this.state;
    const doesUserHaveSharedReports = reports?.length;
    const showDashboard = doesUserHaveSharedReports;

    if (showDashboard) {
      return (
        <Menu.Item>
          <NavLink
            activeClassName="active-light"
            className="nav-item"
            to="/unifiedview/dashboard"
          >
            <span>{titleize(`${t('DASHBOARD')}`)}</span>
          </NavLink>
        </Menu.Item>
      );
    }
  }

  render() {
    if (SessionStore.isQuotesOnly()) {
      return (
        <div className="absolute top-0 left-0 w-full">
          <TrackingHeader />
        </div>
      );
    }

    const brandLogo = (
      <NavLink to="/">
        <img alt="Cargomatic" src={this.state.logo} style={{ height: 25 }} />
      </NavLink>
    );

    const overlay = (
      <NavbarOverlayBanner
        text={SessionStore.getUser().company_name || ''}
        showButton
      />
    );
    const isLoggedInAs = !!SessionStore.getLoginAs();
    const { t } = this.props;
    return (
      <>
        <Navbar
          logo={brandLogo}
          renderRightNav={this.renderRightNav}
          renderMobileNav={this.renderMobileNav}
        >
          <DropdownMenu
            title={t('SHIPMENT', { count: 2 })}
            isDark
            dropDownButtonWidth="w-16"
          >
            {!isMasterAccountUser() && !isApacProd() && (
              <>
                {this.renderMenuItemDashboard()}
                <Menu.Item>
                  <NavLink
                    activeClassName="active-light"
                    className="nav-item"
                    to="/quotes"
                  >
                    <span>{t('GET_QUOTE_SHIPMENT')}</span>
                  </NavLink>
                </Menu.Item>
                <Menu.Item>
                  <NavLink
                    activeClassName="active-light"
                    className="nav-item"
                    to="/booking"
                  >
                    <span>{t('BOOK_SHIPMENT')}</span>
                  </NavLink>
                </Menu.Item>
              </>
            )}

            <Menu.Item>
              <NavLink
                activeClassName="active-light"
                className="nav-item"
                to="/shipments"
              >
                <span>{t('SHIPMENT_HISTORY')}</span>
              </NavLink>
            </Menu.Item>
          </DropdownMenu>
          {!isMasterAccountUser() && (
            <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
              <NavLink className="nav-item" to="/invoices">
                <span>{t('MY_INVOICES')}</span>
              </NavLink>
            </AuthorizationWrapper>
          )}
          {isShipper() && (
            <NavLink className="nav-item" to="/accessorials">
              <span>{t('ACCESSORIAL', { count: 2 })}</span>
            </NavLink>
          )}
          <div className="min-w-full flex flex-row items-center justify-end">
            <div className="w-60">
              <ShipmentSearch />
            </div>
          </div>
        </Navbar>
        <div>{isLoggedInAs && overlay}</div>
      </>
    );
  }
}

export default withTranslation()(ShipperNavigation);
